/**
 * Version-check hook: compares the build hash baked into the JS bundle
 * against the server's current build hash (from /api/version).
 *
 * On mismatch (stale SW cache serving old JS), forces a SW update + hard reload
 * so the user gets the latest deployment without manual cache clearing.
 */
import { useEffect } from 'react'

const CHECK_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes
const RELOAD_KEY = 'version-check-reloaded'

async function checkVersion(): Promise<void> {
  try {
    // Cache-bust the version endpoint to bypass any intermediate caches
    const res = await fetch(`/api/version?_=${Date.now()}`, {
      cache: 'no-store',
    })
    if (!res.ok) return // endpoint may not exist yet during rollout

    const data = await res.json() as { buildHash?: string }
    if (!data.buildHash) return

    if (data.buildHash !== __BUILD_HASH__) {
      // Guard against infinite reload loops: if we already reloaded for this
      // server hash and still have a mismatch, the new SW hasn't activated yet.
      // Don't reload again — wait for the next check interval.
      if (sessionStorage.getItem(RELOAD_KEY) === data.buildHash) {
        console.warn('[version-check] Already reloaded for this hash, waiting for SW activation')
        return
      }

      console.info(
        `[version-check] Build mismatch: client=${__BUILD_HASH__} server=${data.buildHash}. Updating SW and reloading.`,
      )

      // Force SW to check for updates and activate immediately
      const registrations = await navigator.serviceWorker?.getRegistrations()
      if (registrations) {
        await Promise.all(registrations.map((r) => r.update()))
      }

      // Record the hash we're reloading for, then reload after a brief delay
      sessionStorage.setItem(RELOAD_KEY, data.buildHash)
      setTimeout(() => {
        window.location.reload()
      }, 1000)
    } else {
      // Hashes match — clear any stale reload guard
      sessionStorage.removeItem(RELOAD_KEY)
    }
  } catch {
    // Network errors are fine — user is offline or endpoint not deployed yet
  }
}

export function useVersionCheck(): void {
  useEffect(() => {
    // Initial check shortly after mount (give the app time to render)
    const initialTimeout = setTimeout(checkVersion, 5000)

    // Periodic checks while the tab is open
    const interval = setInterval(checkVersion, CHECK_INTERVAL_MS)

    // Also check when the tab becomes visible again (user returns from another tab)
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        checkVersion()
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      clearTimeout(initialTimeout)
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [])
}
